import {
  IDisplayedMeasureSetting,
  IDisplayedSliceSetting,
  IParsedChartData,
} from '../../interfaces';
import { IBreakdownOverallStatus } from '../../../../utils/interfaces';
import { QueryType } from '../../../../utils/apollo/graphqlSchema';
import { IAnalysisColorConfig } from './types';
import {
  mainBlueCustomColor,
  measureColors,
  sliceColors,
} from './customColors';
import { theme } from '../../../../styles/theme';

const isShouldUseMeasureColor = (
  isBreakdownOverall: IBreakdownOverallStatus,
): boolean => {
  return (
    isBreakdownOverall === IBreakdownOverallStatus.NoBreakdown ||
    isBreakdownOverall === IBreakdownOverallStatus.NoBreakdownCompare
  );
};

interface IGenerateAnalysisColorConfigArgs {
  displayedMeasureSettings: IDisplayedMeasureSetting[];
  displayedSliceSettings: IDisplayedSliceSetting[];
  chartData: IParsedChartData | null;
  isBreakdownOverall: IBreakdownOverallStatus;
  hasOverall: boolean;
}

const getMeasureColors = (
  isBreakdownOverall: IBreakdownOverallStatus,
  displayedMeasureSettings: IDisplayedMeasureSetting[],
): IAnalysisColorConfig['measures'] => {
  return displayedMeasureSettings.reduce<Record<string, string>>(
    (acc, dms, index) => {
      const { key, color } = dms;

      // Rules:
      // 1). Custom color;
      // 2). mainBlue color:
      // 2).a. NoBreakdown or NoBreakdownCompare
      // 2).b. When its Funnel step except TimeToConvert mode;
      // 3). Color from measureColors taken by index;
      if (!acc[key]) {
        let resultColor: string = '';

        if (color) {
          resultColor = color;
        } else if (!isShouldUseMeasureColor(isBreakdownOverall)) {
          resultColor = mainBlueCustomColor.hex;
        } else {
          resultColor = measureColors[index % measureColors.length].hex;
        }

        acc[key] = resultColor;
      }

      return acc;
    },
    {},
  );
};

const getSliceColors = (
  chartData: IParsedChartData | null,
  displayedSliceSettings: IDisplayedSliceSetting[],
  hasOverall: boolean,
  isBreakdownOverall: IBreakdownOverallStatus,
): IAnalysisColorConfig['slices'] => {
  const customColors = [...sliceColors];

  // mainBlue color would be added to custom colors for:
  // 1. breakdown+overall
  // 2. compare mode without breakdown
  if (
    hasOverall ||
    isBreakdownOverall === IBreakdownOverallStatus.NoBreakdownCompare
  ) {
    customColors.unshift(mainBlueCustomColor);
  }

  return displayedSliceSettings.reduce<Record<string, string>>(
    (acc, dss, index) => {
      const { key, color } = dss;

      // Rules:
      // 1). Custom color;
      // 2). mainBlue color for groups in Path;
      // 3). Color from customColors taken by index;
      if (!acc[key]) {
        let resultColor: string = '';

        if (color) {
          resultColor = color;
        } else if (chartData && chartData?.queryType === QueryType.Path) {
          resultColor = theme.colors.mainBlue;
        } else {
          resultColor = customColors[index % customColors.length].hex;
        }

        acc[key] = resultColor;
      }

      return acc;
    },
    {},
  );
};

/**
 * Generates color config for analysis.
 * Returns an object with 2 values: measures and slices.
 * Each value is a lookup:
 * [measureKey]: '#rrggbb' for measures;
 * [sliceKey]: '#rrggbb' for slices;
 */
const generateAnalysisColorConfig = ({
  displayedMeasureSettings,
  displayedSliceSettings,
  chartData,
  isBreakdownOverall,
  hasOverall,
}: IGenerateAnalysisColorConfigArgs): IAnalysisColorConfig => {
  const measures = getMeasureColors(
    isBreakdownOverall,
    displayedMeasureSettings,
  );

  const slices = getSliceColors(
    chartData,
    displayedSliceSettings,
    hasOverall,
    isBreakdownOverall,
  );

  return {
    measures,
    slices,
  };
};

const getMeasureColorFromColorConfig = (
  colorConfig: IAnalysisColorConfig,
  key: string,
) => {
  const colorFromConfig = colorConfig.measures?.[key];

  if (colorFromConfig) {
    return colorFromConfig;
  }

  return theme.colors.mainBlue;
};

const getSliceColorFromColorConfig = (
  colorConfig: IAnalysisColorConfig,
  key: string,
) => {
  const colorFromConfig = colorConfig.slices?.[key];

  if (colorFromConfig) {
    return colorFromConfig;
  }

  return theme.colors.secondary900;
};

export {
  isShouldUseMeasureColor,
  generateAnalysisColorConfig,
  getMeasureColorFromColorConfig,
  getSliceColorFromColorConfig,
};
